// ─── Validation de saisie d'un palier prop firm, AVANT enregistrement ─────────
//
// Module PUR (pas de React, pas de Wasp, pas de Prisma) : calcule la liste des
// problemes d'un palier plutot que de laisser `handleSubmit` faire des `return`
// silencieux. Trois niveaux de gravite, actes avec le product owner :
//
// - Requis STRUCTUREL (le label) : toujours bloquant, palier neuf ou existant.
// - Requis METIER (accountSize, objectif de profit, type de drawdown) :
//   bloquant seulement sur un palier NEUF (sans `id`). Sur un palier EXISTANT,
//   simple avertissement.
// - Incoherence de REGLES (via `validateTierRules`) : toujours bloquante.
//
// POURQUOI l'asymetrie sur les requis metier : des paliers sans `accountSize`
// existent deja en base — `propFirmEngine.ts:236` a un repli explicite
// (`tier?.accountSize ?? initialBalance`) et `tradovate/tradovateOperations.ts:318`
// filtre sur `accountSize: { not: null }`. Bloquer leur edition transformerait
// une correction d'un caractere en corvee, sans gain de surete puisque la
// donnee fautive est deja enregistree. Donc : bloquer la CREATION de donnee
// fautive neuve, avertir sur l'existante.
//
// POURQUOI l'incoherence de regles reste bloquante dans les deux cas : elle ne
// signale pas une donnee ABSENTE mais CONTRADICTOIRE, qui produit un faux
// negatif silencieux (un compte cramé jamais detecte). C'est l'incident des
// 18 comptes Lucid documente dans `tierRuleValidation.ts`.

import { validateTierRules } from "../../../trading-accounts/tierRuleValidation";
import { tierFormToData, parseNumericInput, type TierForm } from "./propFirmTierForm";
import type { PricingModel } from "../../../trading-accounts/pricingModel";

export interface FormProblem {
  /** Champ fautif, pour cibler l'input a surligner. */
  field: string;
  message: string;
  /**
   * `true` empeche l'enregistrement. Les requis metier ne sont bloquants que
   * sur un palier NEUF : un palier deja en base reste modifiable, sinon une
   * correction mineure exigerait de completer des champs dont l'admin n'a
   * peut-etre pas la valeur sous la main.
   */
  blocking: boolean;
}

/** Une regle est-elle renseignee ? */
function hasRule(rule: { value: string }): boolean {
  return rule.value.trim() !== "";
}

export function collectTierProblems(tier: TierForm, pricingModel: PricingModel = "one_time"): FormProblem[] {
  const problems: FormProblem[] = [];
  const isNew = tier.id === undefined;
  // Direct funded saute le challenge : la section entiere est masquee cote
  // UI (TierEditor), un objectif de profit n'a donc pas de champ a l'ecran ou
  // le renseigner. Le signaler bloquerait la sauvegarde sur un champ invisible.
  const directFunded = pricingModel === "direct_funded";

  if (tier.label.trim() === "") {
    problems.push({ field: "label", message: "Le label est obligatoire.", blocking: true });
  }

  if (tier.accountSize.trim() === "") {
    problems.push({
      field: "accountSize",
      message: isNew
        ? "La taille du compte est obligatoire : sans elle, l'objectif de challenge est calcule sur le solde initial, qui absorbe le PnL anterieur a la connexion."
        : "Palier incomplet : sans taille de compte, l'objectif de challenge retombe sur le solde initial.",
      blocking: isNew,
    });
  }

  if (!directFunded && !hasRule(tier.profitTarget)) {
    problems.push({
      field: "profitTarget",
      message: isNew
        ? "Un objectif de profit est obligatoire."
        : "Palier incomplet : aucun objectif de profit, la progression de challenge reste a zero.",
      blocking: isNew,
    });
  }

  if (tier.drawdownType === "") {
    problems.push({
      field: "drawdownType",
      message: isNew
        ? "Le type de drawdown est obligatoire."
        : "Palier incomplet : aucun type de drawdown, aucune violation ne sera detectee.",
      blocking: isNew,
    });
  }

  // Un drawdown "static" ne passe JAMAIS par la branche trailing : il n'est
  // donc pas couvert par `validateTierRules` (son `declaresTrailing()` exclut
  // deliberement "static"). Cote moteur, `maxTotalLossResolved` derive de
  // `maxTotalLossAmount ?? maxTotalLossPercent` (`propFirmEngine.ts:444-448`) ;
  // si les deux sont vides il reste `null` et tout le bloc d'application de la
  // regle est saute (`propFirmEngine.ts:451`, `if (maxTotalLossResolved !== null)`).
  // Un palier "static" sans montant NI pourcentage declare donc une regle de
  // perte totale qui ne sera jamais appliquee : aucune violation ne sera
  // jamais detectee, silencieusement et en faveur du trader.
  if (tier.drawdownType === "static" && !hasRule(tier.maxTotalLoss)) {
    problems.push({
      field: "maxTotalLoss",
      message: isNew
        ? "Le drawdown est de type Perte totale (static) : un montant ou un pourcentage de Max Total Loss est obligatoire, sinon aucune regle de perte ne sera appliquee."
        : "Palier incomplet : drawdown static sans montant ni pourcentage de Max Total Loss, aucune violation ne sera jamais detectee. Renseignez le montant ou le pourcentage de Max Total Loss.",
      blocking: isNew,
    });
  }

  // PIEGE CORRIGE ICI (revue Task 12) : `tierFormToData` ecrit
  // `price: parseNumericInput(f.price)`, qui rend `null` sur toute saisie
  // AMBIGUE ("1,080" separateur de milliers, "1.299" trois decimales, "$99"
  // symbole colle) -- exactement le mode silencieux que ce module existe
  // pour interdire ailleurs (cf. accountSize plus haut). Sans ce garde, un
  // prix illisible etait ecrit tel quel : `null`, effacant silencieusement un
  // prix pose par le CSV du referentiel ou une saisie precedente. Bloquant
  // dans TOUS les cas (neuf ou existant), au meme titre que les incoherences
  // de regles : ce n'est pas une donnee absente, c'est une saisie presente
  // mais MECONNUE du parseur, qui serait sinon silencieusement perdue.
  if (tier.price.trim() !== "" && parseNumericInput(tier.price) === null) {
    problems.push({
      field: "price",
      message: "Prix illisible : saisissez un nombre sans séparateur de milliers (ex : 1080 ou 99.5).",
      blocking: true,
    });
  }

  // Coherence des regles : module deja existant et teste. Toujours bloquant,
  // y compris sur un palier existant, parce qu'il ne signale pas une donnee
  // ABSENTE mais une donnee CONTRADICTOIRE, qui produit un faux negatif
  // silencieux (cf. les 18 comptes Lucid archives "Challenge valide").
  for (const problem of validateTierRules(tierFormToData(tier, tier.sortOrder))) {
    problems.push({
      field: problem.field === "trailingDrawdownAmount" ? "trailingDrawdown" : problem.field,
      message: problem.message,
      blocking: true,
    });
  }

  return problems;
}

/** Le palier empeche-t-il l'enregistrement ? */
export function isTierBlocking(tier: TierForm, pricingModel: PricingModel = "one_time"): boolean {
  return collectTierProblems(tier, pricingModel).some((p) => p.blocking);
}
